<template>
  <div class="section">
    <b-loading v-if="isloading" :is-full-page="true"/>

    <div v-else class="container">
      <div class="buttons is-right">
        <b-button type="is-primary" :disabled="!hasShippings" @click="exportScanShippings">
          {{ $t('global.export') }}
        </b-button>
      </div>

      <ag-grid-vue
        style="width: 100%; height: 1000px;"
        class="ag-theme-alpine"
        :modules="modules"
        :pagination="true"
        :pagination-auto-page-size="true"
        :grid-options="gridOptions"
        :row-data="shippings"
        :column-defs="columnDefs"
        @row-clicked="showBarcodes"
      />

      <b-modal :active.sync="showingBarcodes" scroll="keep" has-modal-card>
        <div class="modal-card">
          <header class="modal-card-head">
            <p class="modal-card-title">
              {{ selectedShippingTitle }}
            </p>
          </header>
          <section class="modal-card-body">
            <div v-if="selectedBarcodes.length" class="barcode-list">
              <span
                v-for="barcode in selectedBarcodes"
                :key="barcode"
                class="tag is-link is-light barcode-tag"
              >
                {{ barcode }}
              </span>
            </div>
            <p v-else>
              No barcodes
            </p>
          </section>
          <footer class="modal-card-foot">
            <button class="button is-fullwidth" @click="showingBarcodes = false">
              {{ $t('global.close') }}
            </button>
          </footer>
        </div>
      </b-modal>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { AllModules } from '@ag-grid-enterprise/all-modules'
import XLSX from 'xlsx'
import ShippingManagement from '@/services/v2/shippingManagement'

export default {
  data () {
    return {
      modules: AllModules,
      groupDisplayType: 'custom',
      isloading: false,
      shippings: [],
      selectedShipping: null,
      showingBarcodes: false
    }
  },
  computed: {
    ...mapState({
      user: state => state.auth.user,
      selectedCustomer: state => state.selectedCustomer
    }),
    gridOptions () {
      return {
        defaultColDef: {
          sortable: true,
          filter: true,
          resizable: true
        },
        getContextMenuItems: this.getContextMenuItems,
        rowClass: 'clickable-row'
      }
    },
    columnDefs () {
      return [
        {
          headerName: this.$t('customer.shipping_management.date_table.date'),
          field: 'creationDate'
        },
        {
          headerName: this.$t('customer.shipping_management.hours_table.consignee'),
          field: 'consigneeCode'
        },
        {
          headerName: this.$t('customer.shipping_management.hours_table.enduserName'),
          field: 'enduserName'
        },
        {
          headerName: this.$t('customer.shipping_management.hours_table.enduserAddress'),
          field: 'enduserAddress'
        },
        {
          headerName: this.$t('customer.shipping_management.hours_table.reference'),
          field: 'reference'
        },
        {
          headerName: this.$t('customer.shipping_management.hours_table.reference_complement'),
          field: 'referenceComplement'
        },
        {
          headerName: this.$t('customer.shipping_management.hours_table.barcode_qty'),
          field: 'quantity',
          filter: 'agNumberColumnFilter'
        }
      ]
    },
    hasShippings () {
      return !!(this.shippings && this.shippings.length)
    },
    selectedBarcodes () {
      if (!this.selectedShipping || !this.selectedShipping.crateMovements) return []
      return this.selectedShipping.crateMovements
        .map(crateMovement => crateMovement.barcode)
        .filter(Boolean)
    },
    selectedShippingTitle () {
      if (!this.selectedShipping) return this.$t('sidebar.scan_shipping')
      const reference = this.selectedShipping.reference || this.$t('customer.shipping_management.hours_table.unspecified')
      return `${reference} - ${this.selectedShipping.quantity} ${this.$t('customer.shipping_management.hours_table.barcode_qty')}`
    }
  },
  watch: {
    selectedCustomer (newValue, oldValue) {
      this.loadData()
    }
  },
  mounted () {
    this.loadData()
  },
  methods: {
    async loadData () {
      if (!this.selectedCustomer) return
      this.isloading = true
      try {
        const [shippings, crateMovements] = await Promise.all([
          ShippingManagement.getShippings(this.selectedCustomer),
          ShippingManagement.getCrateMovementsByCustomerId(this.selectedCustomer)
        ])

        this.shippings = (shippings || []).map(shipping => {
          const shippingCrateMovements = (crateMovements || []).filter(crateMovement => {
            return crateMovement.shipping_id === shipping.id || crateMovement.shippingId === shipping.id
          })

          return {
            ...shipping,
            rawCreationDate: shipping.creationDate || shipping.creation_date,
            creationDate: this.formatLocalDateTime(shipping.creationDate || shipping.creation_date),
            crateMovements: shippingCrateMovements,
            quantity: shippingCrateMovements.length
          }
        }).sort((a, b) => new Date(b.rawCreationDate) - new Date(a.rawCreationDate))
      } catch (error) {
        this.$buefy.toast.open({
          message: this.$t('customer.scan_mvt.errors.load_data'),
          type: 'is-danger',
          duration: 5000
        })
      } finally {
        this.isloading = false
      }
    },
    formatLocalDate (value) {
      if (!value) return ''
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return ''
      return date.toLocaleDateString()
    },
    formatLocalDateTime (value) {
      if (!value) return ''
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return ''
      return date.toLocaleString()
    },
    showBarcodes (params) {
      if (!params || !params.data) return
      this.selectedShipping = params.data
      this.showingBarcodes = true
    },
    getContextMenuItems (params) {
      return (params.defaultItems || []).filter(item => item !== 'export')
    },
    exportScanShippings () {
      if (!this.hasShippings) return

      const columns = [
        { header: this.$t('customer.shipping_management.date_table.date'), field: 'creationDate' },
        { header: this.$t('customer.shipping_management.edit_modal.crates'), field: 'barcodes' },
        { header: this.$t('customer.shipping_management.hours_table.consignee'), field: 'consigneeCode' },
        { header: this.$t('customer.shipping_management.hours_table.enduserName'), field: 'enduserName' },
        { header: this.$t('customer.shipping_management.hours_table.enduserAddress'), field: 'enduserAddress' },
        { header: this.$t('customer.shipping_management.hours_table.reference'), field: 'reference' },
        { header: this.$t('customer.shipping_management.hours_table.reference_complement'), field: 'referenceComplement' }
      ]
      const data = [
        columns.map(column => column.header),
        ...this.buildExportRows(columns)
      ]
      const worksheet = XLSX.utils.aoa_to_sheet(data)
      const workbook = XLSX.utils.book_new()
      const fileDate = new Date().toISOString().slice(0, 10)

      XLSX.utils.book_append_sheet(workbook, worksheet, 'Scan shipping')
      XLSX.writeFile(workbook, `GPS-${this.$t('sidebar.scan_shipping')}-${fileDate}.csv`)
    },
    buildExportRows (columns) {
      return this.shippings.flatMap(shipping => {
        const barcodes = this.exportBarcodes(shipping)
        const rowBarcodes = barcodes.length ? barcodes : ['']

        return rowBarcodes.map(barcode => {
          return columns.map(column => this.formatExportValue(shipping, column.field, barcode))
        })
      })
    },
    exportBarcodes (shipping) {
      return (shipping.crateMovements || []).map(crateMovement => crateMovement.barcode).filter(Boolean)
    },
    formatExportValue (shipping, field, barcode) {
      if (field === 'barcodes') {
        return barcode || ''
      }
      return shipping[field] || ''
    }
  }
}
</script>

<style scoped>
.barcode-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.barcode-tag {
  margin: 0;
}

::v-deep .clickable-row {
  cursor: pointer;
}
</style>
